<template>
  <div class="product-guide-page" v-loading="loading">
    <div v-if="view === 'search'" class="guide-search">
      <section class="query-card">
        <button type="button" class="leaderboard-entry" @click="openLeaderboard">
          <el-icon><Trophy /></el-icon>
          贡献榜
        </button>

        <header class="query-heading">
          <h2>多产品</h2>
          <p>输入接头序列号前5位，快速查询对应产品攻略</p>
        </header>

        <div class="search-row">
          <el-input
            v-model="prefix"
            maxlength="5"
            clearable
            show-word-limit
            placeholder="请输入接头序列号前5位"
            class="sn-input"
            @keyup.enter="handleSearch"
          >
            <template #prefix>
              <el-icon class="sn-input-icon"><Search /></el-icon>
            </template>
          </el-input>
          <el-button
            type="primary"
            class="search-submit"
            :disabled="!canSearch"
            :loading="loading"
            @click="handleSearch"
          >
            查询
          </el-button>
        </div>

        <div v-if="recentPrefixes.length" class="recent-searches">
          <span class="recent-label">最近查询</span>
          <div class="recent-tags">
            <button
              v-for="item in recentPrefixes"
              :key="item"
              type="button"
              class="recent-tag"
              :class="{ 'is-current': item === prefix }"
              @click="searchRecent(item)"
            >
              {{ item }}
            </button>
          </div>
        </div>

        <Transition name="match-panel">
          <div v-if="searchStatus === 'empty'" class="search-empty">
            <p>未找到该SN对应的产品</p>
            <p>请确认序列号前5位是否正确</p>
          </div>
        </Transition>
      </section>

      <Transition name="match-panel">
        <section v-if="searchStatus === 'multiple'" class="result-card">
          <p class="match-tip">找到 {{ matchOptions.length }} 个匹配产品，请选择实际产品</p>
          <div class="match-list">
            <button
              v-for="item in matchOptions"
              :key="item.id"
              type="button"
              class="match-row"
              @click="selectMatchedProduct(item.id)"
            >
              <span>{{ item.name }}</span>
              <el-icon class="match-arrow"><ArrowRight /></el-icon>
            </button>
          </div>
        </section>
      </Transition>
    </div>

    <div v-else-if="currentProduct" class="guide-detail">
      <header class="detail-header">
        <div class="detail-toolbar">
          <button type="button" class="nav-back" @click="handleBack">
            <span class="nav-back-icon">
              <el-icon><ArrowLeft /></el-icon>
            </span>
            返回查询
          </button>
          <div class="detail-toolbar-right">
            <button type="button" class="toolbar-add" @click="openAddDialog">
              <el-icon><Plus /></el-icon>
              新增攻略
            </button>
            <button type="button" class="toolbar-history" @click="historyOpen = true">
              <el-icon><Clock /></el-icon>
              编辑历史
            </button>
          </div>
        </div>
        <div class="detail-overview">
          <div class="detail-identity">
            <h2>{{ currentProduct.name }}</h2>
            <div class="detail-meta-row">
              <button type="button" class="category-edit" @click="openCategoryDialog">
                {{ currentProduct.category }}
                <span>修改</span>
              </button>
              <span class="detail-prefix">
                <small>SN</small>
                {{ currentProduct.prefix }}
              </span>
            </div>
          </div>
          <div class="complete-block">
            <div class="complete-head">
              <span>攻略完善度</span>
              <span>{{ completeness.filled }} / {{ completeness.total }}</span>
            </div>
            <el-progress
              :percentage="completeness.percent"
              :stroke-width="5"
              :show-text="true"
            />
          </div>
        </div>
      </header>

      <div class="detail-grid">
        <section class="detail-group is-software">
          <h3><i></i>{{ softwareGroup.title }}</h3>
          <div
            v-for="field in softwareFields"
            :key="field.key"
            class="guide-field-row"
            :class="{ 'is-editing': isEditing(field.key) }"
          >
            <div class="field-name">
              {{ field.label }}
              <small v-if="field.custom" class="custom-mark">自定义</small>
            </div>
            <div class="field-body">
              <template v-if="isEditing(field.key)">
                <el-select
                  v-if="field.control === 'select'"
                  v-model="editDraft"
                  clearable
                  :placeholder="field.placeholder || '请选择'"
                  class="full-control"
                >
                  <el-option
                    v-for="option in selectOptionsFor(field)"
                    :key="option"
                    :label="option"
                    :value="option"
                  />
                </el-select>
                <el-input
                  v-else-if="field.control === 'input'"
                  v-model="editDraft"
                  clearable
                />
                <el-input
                  v-else
                  v-model="editDraft"
                  type="textarea"
                  :autosize="{ minRows: 4, maxRows: 12 }"
                />
                <div class="edit-actions">
                  <el-button size="small" @click="cancelFieldEdit">取消</el-button>
                  <el-button size="small" type="primary" @click="saveField">保存</el-button>
                </div>
              </template>
              <template v-else>
                <div class="field-value" :class="{ 'is-empty': isFieldEmpty(field.key) }">
                  {{ isFieldEmpty(field.key) ? '未填写' : fieldValue(field.key) }}
                </div>
                <div v-if="fieldMeta(field.key)" class="field-meta">{{ fieldMeta(field.key) }}</div>
              </template>
            </div>
            <div v-if="!isEditing(field.key)" class="field-actions">
              <button
                v-if="field.copyable && !isFieldEmpty(field.key)"
                type="button"
                class="field-action"
                title="复制"
                @click="copyField(field)"
              >
                复制
              </button>
              <button type="button" class="field-action is-primary" title="编辑" @click="openFieldEditor(field)">
                编辑
              </button>
              <button
                v-if="field.custom"
                type="button"
                class="field-action"
                title="删除"
                @click="confirmDeleteField(field)"
              >
                删除
              </button>
            </div>
          </div>
        </section>

        <section class="detail-group is-logs">
          <h3><i></i>{{ logsGroup.title }}</h3>
          <div
            v-for="field in logsFields"
            :key="field.key"
            class="guide-field-row is-path"
            :class="{ 'is-editing': isEditing(field.key) }"
          >
            <div class="field-name">
              {{ field.label }}
              <small v-if="field.custom" class="custom-mark">自定义</small>
            </div>
            <div class="field-body">
              <template v-if="isEditing(field.key)">
                <el-input v-model="editDraft" clearable />
                <div class="edit-actions">
                  <el-button size="small" @click="cancelFieldEdit">取消</el-button>
                  <el-button size="small" type="primary" @click="saveField">保存</el-button>
                </div>
              </template>
              <template v-else>
                <div class="field-value is-path" :class="{ 'is-empty': isFieldEmpty(field.key) }">
                  {{ isFieldEmpty(field.key) ? '未填写' : fieldValue(field.key) }}
                </div>
                <div v-if="fieldMeta(field.key)" class="field-meta">{{ fieldMeta(field.key) }}</div>
              </template>
            </div>
            <div v-if="!isEditing(field.key)" class="field-actions">
              <button
                v-if="field.copyable && !isFieldEmpty(field.key)"
                type="button"
                class="field-action"
                title="复制"
                @click="copyField(field)"
              >
                复制
              </button>
              <button type="button" class="field-action is-primary" title="编辑" @click="openFieldEditor(field)">
                编辑
              </button>
              <button
                v-if="field.custom"
                type="button"
                class="field-action"
                title="删除"
                @click="confirmDeleteField(field)"
              >
                删除
              </button>
            </div>
          </div>
        </section>

        <section class="detail-group is-usage">
          <h3><i></i>{{ usageGroup.title }}</h3>
          <div
            v-for="field in usageTextFields"
            :key="field.key"
            class="guide-field-row"
            :class="{ 'is-editing': isEditing(field.key) }"
          >
            <div class="field-name">
              {{ field.label }}
              <small v-if="field.custom" class="custom-mark">自定义</small>
            </div>
            <div class="field-body">
              <template v-if="isEditing(field.key)">
                <el-select
                  v-if="field.control === 'select'"
                  v-model="editDraft"
                  clearable
                  :placeholder="field.placeholder || '请选择'"
                  class="full-control"
                >
                  <el-option
                    v-for="option in selectOptionsFor(field)"
                    :key="option"
                    :label="option"
                    :value="option"
                  />
                </el-select>
                <el-input v-else-if="field.control === 'input'" v-model="editDraft" clearable />
                <el-input
                  v-else
                  v-model="editDraft"
                  type="textarea"
                  :autosize="{ minRows: 4, maxRows: 12 }"
                />
                <div class="edit-actions">
                  <el-button size="small" @click="cancelFieldEdit">取消</el-button>
                  <el-button size="small" type="primary" @click="saveField">保存</el-button>
                </div>
              </template>
              <template v-else>
                <div class="field-value" :class="{ 'is-path': field.copyable, 'is-empty': isFieldEmpty(field.key) }">
                  {{ isFieldEmpty(field.key) ? '未填写' : fieldValue(field.key) }}
                </div>
                <div v-if="fieldMeta(field.key)" class="field-meta">{{ fieldMeta(field.key) }}</div>
              </template>
            </div>
            <div v-if="!isEditing(field.key)" class="field-actions">
              <button
                v-if="field.copyable && !isFieldEmpty(field.key)"
                type="button"
                class="field-action"
                title="复制"
                @click="copyField(field)"
              >
                复制
              </button>
              <button type="button" class="field-action is-primary" title="编辑" @click="openFieldEditor(field)">
                编辑
              </button>
              <button
                v-if="field.custom"
                type="button"
                class="field-action"
                title="删除"
                @click="confirmDeleteField(field)"
              >
                删除
              </button>
            </div>
          </div>
          <div
            v-for="field in usageTextareaFields"
            :key="field.key"
            class="usage-block"
            :class="{ 'is-editing': isEditing(field.key) }"
          >
            <div class="usage-head">
              <span>
                {{ field.label }}
                <small v-if="field.custom" class="custom-mark">自定义</small>
              </span>
              <div v-if="!isEditing(field.key)" class="field-actions">
                <button type="button" class="field-action is-primary" title="编辑" @click="openFieldEditor(field)">
                  编辑
                </button>
                <button
                  v-if="field.custom"
                  type="button"
                  class="field-action"
                  title="删除"
                  @click="confirmDeleteField(field)"
                >
                  删除
                </button>
              </div>
            </div>
            <template v-if="isEditing(field.key)">
              <el-input
                v-model="editDraft"
                type="textarea"
                :autosize="{ minRows: 4, maxRows: 12 }"
              />
              <div class="edit-actions">
                <el-button size="small" @click="cancelFieldEdit">取消</el-button>
                <el-button size="small" type="primary" @click="saveField">保存</el-button>
              </div>
            </template>
            <template v-else>
              <div class="field-value" :class="{ 'is-empty': isFieldEmpty(field.key) }">
                {{ isFieldEmpty(field.key) ? '未填写' : fieldValue(field.key) }}
              </div>
              <div v-if="fieldMeta(field.key)" class="field-meta">{{ fieldMeta(field.key) }}</div>
            </template>
          </div>
        </section>
      </div>
    </div>

    <el-dialog v-model="addOpen" title="新增攻略内容" width="460px" append-to-body>
      <el-form label-position="top" class="add-form">
        <el-form-item label="所属分类">
          <el-select v-model="addForm.group" class="full-control">
            <el-option
              v-for="item in GUIDE_GROUP_OPTIONS"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="标题">
          <el-input v-model="addForm.label" maxlength="30" placeholder="例如：固件升级说明" />
        </el-form-item>
        <el-form-item label="内容">
          <el-input
            v-model="addForm.value"
            type="textarea"
            :autosize="{ minRows: 4, maxRows: 10 }"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addOpen = false">取消</el-button>
        <el-button type="primary" @click="addCustomField">添加</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="categoryOpen" title="修改大类" width="420px" append-to-body>
      <el-radio-group v-model="selectedCategory" class="category-list">
        <el-radio
          v-for="item in categories"
          :key="item"
          :value="item"
          :label="item"
        >
          {{ item }}
        </el-radio>
      </el-radio-group>
      <div class="create-row">
        <el-input
          v-model="newCategoryName"
          maxlength="20"
          placeholder="新建大类名称"
          @keyup.enter="createCategory"
        />
        <el-button type="primary" plain @click="createCategory">+ 新建大类</el-button>
      </div>
      <template #footer>
        <el-button @click="categoryOpen = false">取消</el-button>
        <el-button type="primary" :disabled="!selectedCategory" @click="saveCategory">确定</el-button>
      </template>
    </el-dialog>

    <el-drawer
      v-model="leaderboardOpen"
      direction="rtl"
      size="420px"
      append-to-body
      class="leaderboard-drawer"
    >
      <template #header>
        <div class="board-heading">
          <span class="board-heading-icon">
            <el-icon><Trophy /></el-icon>
          </span>
          <div class="board-heading-text">
            <h3>贡献榜</h3>
            <p>完善攻略，点亮荣誉</p>
          </div>
        </div>
      </template>

      <div class="board-body" v-loading="leaderboardLoading">
        <div class="board-podium">
          <div
            v-for="item in leaderboard.ranking.slice(0, 3)"
            :key="item.rank"
            class="podium-card"
            :class="medalClass(item.rank)"
          >
            <el-avatar
              class="podium-avatar"
              :class="{ 'has-photo': Boolean(guideAvatar(item)) }"
              :size="item.rank === 1 ? 52 : 40"
              :src="guideAvatar(item)"
            >
              {{ guideInitial(item.name) }}
            </el-avatar>
            <strong class="podium-score">{{ item.score }}</strong>
            <span class="podium-name">{{ item.name }}</span>
            <small class="podium-rank">第 {{ item.rank }} 名</small>
          </div>
        </div>

        <div v-if="leaderboard.ranking.length > 3" class="board-list">
          <div class="board-list-label">其他贡献者</div>
          <div
            v-for="item in leaderboard.ranking.slice(3)"
            :key="item.rank"
            class="board-row"
          >
            <span class="rank">{{ item.rank }}</span>
            <span class="board-user">
              <el-avatar
                class="board-avatar"
                :class="{ 'has-photo': Boolean(guideAvatar(item)) }"
                :size="28"
                :src="guideAvatar(item)"
              >
                {{ guideInitial(item.name) }}
              </el-avatar>
              <span class="name">{{ item.name }}</span>
            </span>
            <strong class="score">{{ item.score }}</strong>
          </div>
        </div>

        <div class="board-mine">
          <div class="board-mine-kicker">我的贡献</div>
          <div class="board-mine-main">
            <div class="board-mine-item">
              <span>排名</span>
              <strong>第 {{ leaderboard.mine.rank }} 名</strong>
            </div>
            <div class="board-mine-item">
              <span>贡献值</span>
              <strong class="is-score">{{ leaderboard.mine.score }}</strong>
            </div>
          </div>
          <p class="board-mine-hint">继续完善攻略，荣誉正在靠近</p>
        </div>
      </div>
    </el-drawer>

    <el-drawer
      v-model="historyOpen"
      direction="rtl"
      size="400px"
      append-to-body
      class="history-drawer"
    >
      <template #header>
        <div class="board-heading">
          <span class="history-heading-icon">
            <el-icon><Clock /></el-icon>
          </span>
          <div class="board-heading-text">
            <h3>编辑历史</h3>
            <p>每一次完善都被记住</p>
          </div>
        </div>
      </template>

      <el-empty v-if="!currentProduct?.history?.length" description="暂无编辑历史" />
      <div v-else class="history-timeline">
        <div
          v-for="(item, index) in currentProduct.history"
          :key="`${item.time}-${index}`"
          class="history-item"
          :class="{ 'is-latest': index === 0 }"
        >
          <el-avatar class="history-avatar" :size="32" :src="historyAvatar(item.editor)">
            {{ guideInitial(item.editor) }}
          </el-avatar>
          <div class="history-body">
            <div class="history-top">
              <strong>{{ item.editor }}</strong>
              <small>{{ formatHistoryTime(item.time) }}</small>
            </div>
            <p class="history-action">
              修改了
              <span class="history-field">{{ item.fieldLabel }}</span>
            </p>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { ElMessage, ElMessageBox } from 'element-plus';
import { ArrowLeft, ArrowRight, Clock, Plus, Search, Trophy } from '@element-plus/icons-vue';
import {
  addProductGuideCategory,
  addProductGuideCustomField,
  deleteProductGuideCustomField,
  getProductGuideCategories,
  getProductGuideLeaderboard,
  saveProductGuideCategory,
  saveProductGuideField,
  searchProductGuides,
} from '@/api/users';
import {
  GUIDE_FIELD_GROUPS,
  GUIDE_GROUP_OPTIONS,
  getGuideCompleteness,
  getGuideFieldDef,
  isCompleteGuidePrefix,
  normalizeGuideSelectValue,
} from '@/utils/productGuideMock.mjs';

const store = useStore();
const view = ref('search');
const prefix = ref('');
const loading = ref(false);
const searchStatus = ref('idle');
const currentProduct = ref(null);
const matchOptions = ref([]);
const editFieldKey = ref('');
const editDraft = ref('');
const categoryOpen = ref(false);
const selectedCategory = ref('');
const newCategoryName = ref('');
const addOpen = ref(false);
const addForm = ref({
  group: 'software',
  label: '',
  value: '',
});
const leaderboardOpen = ref(false);
const leaderboardLoading = ref(false);
const leaderboard = ref({ ranking: [], mine: { rank: 0, score: 0, avatar: '' } });
const historyOpen = ref(false);
const categories = ref(['未分类']);
const recentPrefixes = ref([]);
const RECENT_PREFIX_KEY = 'product-guide-recent-prefix';
const RECENT_PREFIX_LIMIT = 5;

const softwareGroup = GUIDE_FIELD_GROUPS.find((group) => group.key === 'software');
const logsGroup = GUIDE_FIELD_GROUPS.find((group) => group.key === 'logs');
const usageGroup = GUIDE_FIELD_GROUPS.find((group) => group.key === 'usage');

function responseData(response) {
  return response?.data?.data || {};
}

function errorMessage(error, fallback) {
  if (Number(error?.status) === 404 && error?.info === 'Not Found') {
    return '多产品后端接口未部署或服务未更新';
  }
  return error?.response?.data?.message || error?.response?.data?.error || error?.info || error?.message || fallback;
}

function mapCustomField(item) {
  return {
    key: item.id,
    label: item.label,
    control: 'textarea',
    copyable: false,
    custom: true,
    type: item.type || 'text',
  };
}

function fieldsForGroup(group) {
  const custom = (currentProduct.value?.customFields || [])
    .filter((item) => item.group === group.key)
    .map(mapCustomField);
  return [...group.fields, ...custom];
}

const softwareFields = computed(() => fieldsForGroup(softwareGroup));
const logsFields = computed(() => fieldsForGroup(logsGroup));
const usageTextFields = computed(() => fieldsForGroup(usageGroup).filter((field) => field.control !== 'textarea'));
const usageTextareaFields = computed(() => fieldsForGroup(usageGroup).filter((field) => field.control === 'textarea'));
const canSearch = computed(() => isCompleteGuidePrefix(prefix.value));
const completeness = computed(() => getGuideCompleteness(currentProduct.value));

watch(prefix, (value) => {
  const next = String(value || '').replace(/\D/g, '').slice(0, 5);
  if (next !== value) {
    prefix.value = next;
  }
});

watch(categoryOpen, (open) => {
  if (open) {
    selectedCategory.value = currentProduct.value?.category || '';
    newCategoryName.value = '';
  }
});

function fieldRecord(fieldKey) {
  if (currentProduct.value?.fields?.[fieldKey]) {
    return currentProduct.value.fields[fieldKey];
  }
  return currentProduct.value?.customFields?.find((item) => item.id === fieldKey) || null;
}

function fieldValue(fieldKey) {
  const raw = fieldRecord(fieldKey)?.value || '';
  const fieldDef = getGuideFieldDef(fieldKey);
  if (fieldDef?.control === 'select') {
    return normalizeGuideSelectValue(fieldDef, raw);
  }
  return raw;
}

function selectOptionsFor(field) {
  const options = [...(field?.options || [])];
  const current = String(editDraft.value || '').trim();
  if (current && !options.includes(current)) {
    options.push(current);
  }
  return options;
}

function isFieldEmpty(fieldKey) {
  return !String(fieldValue(fieldKey) || '').trim();
}

function isEditing(fieldKey) {
  return editFieldKey.value === fieldKey;
}

function fieldMeta(fieldKey) {
  const field = fieldRecord(fieldKey);
  if (!field?.lastEditor || !field?.lastEditedAt) {
    return '';
  }
  const shortTime = String(field.lastEditedAt).replace(/^\d{4}-/, '');
  return `${field.lastEditor} · ${shortTime}`;
}

function medalClass(rank) {
  if (rank === 1) return 'is-gold';
  if (rank === 2) return 'is-silver';
  if (rank === 3) return 'is-bronze';
  return '';
}

function guideInitial(name) {
  return String(name || '').slice(0, 1);
}

function guideAvatar(item) {
  const url = String(item?.avatar || item?.imageUrl || item?.avatarUrl || item?.headUrl || '').trim();
  if (url) {
    return url;
  }

  const username = String(store.state.userData?.username || '').trim();
  if (username && item?.name === username) {
    return String(store.state.userData?.imageUrl || '').trim() || undefined;
  }

  return undefined;
}

function historyAvatar(editor) {
  return guideAvatar({ name: editor });
}

function formatHistoryTime(time) {
  return String(time || '').replace(/^\d{4}-/, '').replace(' ', ' · ');
}

function rememberPrefix(value) {
  const next = String(value || '').trim();
  if (!isCompleteGuidePrefix(next)) {
    return;
  }
  recentPrefixes.value = [
    next,
    ...recentPrefixes.value.filter((item) => item !== next),
  ].slice(0, RECENT_PREFIX_LIMIT);
  try {
    localStorage.setItem(RECENT_PREFIX_KEY, JSON.stringify(recentPrefixes.value));
  } catch {
    // 浏览器禁用本地存储时不影响查询。
  }
}

async function loadCategories(showError = false) {
  try {
    const response = await getProductGuideCategories();
    const next = responseData(response).categories;
    if (Array.isArray(next) && next.length) {
      categories.value = next;
    }
  } catch (error) {
    if (showError) {
      ElMessage.error(errorMessage(error, '大类加载失败'));
    }
  }
}

function searchRecent(value) {
  prefix.value = value;
  handleSearch();
}

async function handleSearch() {
  if (!canSearch.value || loading.value) {
    return;
  }

  const queryPrefix = prefix.value;
  loading.value = true;
  searchStatus.value = 'loading';
  matchOptions.value = [];
  currentProduct.value = null;
  view.value = 'search';
  rememberPrefix(queryPrefix);

  try {
    const response = await searchProductGuides(queryPrefix);
    const result = responseData(response);
    const matches = Array.isArray(result.matches) ? result.matches : [];
    if (result.status === 'empty' || !matches.length) {
      searchStatus.value = 'empty';
      return;
    }
    if (result.status === 'unique' || matches.length === 1) {
      searchStatus.value = 'unique';
      currentProduct.value = matches[0];
      view.value = 'detail';
      return;
    }
    searchStatus.value = 'multiple';
    matchOptions.value = matches;
  } catch (error) {
    searchStatus.value = 'idle';
    ElMessage.error(errorMessage(error, '产品攻略查询失败'));
  } finally {
    loading.value = false;
  }
}

function selectMatchedProduct(id) {
  currentProduct.value = matchOptions.value.find((item) => item.id === id) || null;
  if (currentProduct.value) {
    view.value = 'detail';
  }
}

function handleBack() {
  view.value = 'search';
  currentProduct.value = null;
  searchStatus.value = 'idle';
  matchOptions.value = [];
  historyOpen.value = false;
  categoryOpen.value = false;
  addOpen.value = false;
  cancelFieldEdit();
}

function openFieldEditor(field) {
  editFieldKey.value = field.key;
  const current = fieldValue(field.key);
  editDraft.value = field.control === 'select'
    ? normalizeGuideSelectValue(field, current)
    : current;
}

function cancelFieldEdit() {
  editFieldKey.value = '';
  editDraft.value = '';
}

async function saveField() {
  if (!currentProduct.value || !editFieldKey.value) {
    return;
  }
  try {
    const response = await saveProductGuideField(
      currentProduct.value.id,
      editFieldKey.value,
      editDraft.value == null ? '' : String(editDraft.value),
    );
    currentProduct.value = responseData(response).product;
    cancelFieldEdit();
    ElMessage.success('保存成功');
  } catch (error) {
    ElMessage.error(errorMessage(error, '保存失败'));
  }
}

function copyField(field) {
  const value = fieldValue(field.key);
  if (!value) {
    return;
  }

  navigator.clipboard.writeText(value).then(
    () => ElMessage.success('已复制'),
    () => ElMessage.error('复制失败'),
  );
}

function openCategoryDialog() {
  categoryOpen.value = true;
  loadCategories(true);
}

function openAddDialog() {
  addForm.value = {
    group: 'software',
    label: '',
    value: '',
  };
  addOpen.value = true;
}

async function addCustomField() {
  const label = String(addForm.value.label || '').trim();
  if (!label) {
    ElMessage.warning('请输入标题');
    return;
  }
  if (!currentProduct.value) {
    return;
  }
  try {
    const response = await addProductGuideCustomField(currentProduct.value.id, {
      group: addForm.value.group,
      label,
      value: addForm.value.value,
    });
    currentProduct.value = responseData(response).product;
    addOpen.value = false;
    ElMessage.success('保存成功');
  } catch (error) {
    ElMessage.error(errorMessage(error, '添加失败'));
  }
}

function confirmDeleteField(field) {
  ElMessageBox.confirm(`确认删除“${field.label}”？`, '删除', {
    confirmButtonText: '删除',
    cancelButtonText: '取消',
    type: 'warning',
  }).then(async () => {
    try {
      const response = await deleteProductGuideCustomField(currentProduct.value.id, field.key);
      if (isEditing(field.key)) {
        cancelFieldEdit();
      }
      currentProduct.value = responseData(response).product;
      ElMessage.success('已删除');
    } catch (error) {
      ElMessage.error(errorMessage(error, '删除失败'));
    }
  }).catch(() => {});
}

async function createCategory() {
  const name = String(newCategoryName.value || '').trim();
  if (!name) {
    ElMessage.warning('请输入大类名称');
    return;
  }
  try {
    const response = await addProductGuideCategory(name);
    const next = responseData(response).categories;
    if (Array.isArray(next)) {
      categories.value = next;
    }
    selectedCategory.value = name;
    newCategoryName.value = '';
  } catch (error) {
    ElMessage.error(errorMessage(error, '新建大类失败'));
  }
}

async function saveCategory() {
  if (!currentProduct.value || !selectedCategory.value) {
    return;
  }
  try {
    const response = await saveProductGuideCategory(currentProduct.value.id, selectedCategory.value);
    currentProduct.value = responseData(response).product;
    categoryOpen.value = false;
    ElMessage.success('保存成功');
  } catch (error) {
    ElMessage.error(errorMessage(error, '保存失败'));
  }
}

async function openLeaderboard() {
  leaderboardOpen.value = true;
  leaderboardLoading.value = true;
  try {
    const response = await getProductGuideLeaderboard();
    const data = responseData(response);
    leaderboard.value = {
      ranking: Array.isArray(data.ranking) ? data.ranking : [],
      mine: data.mine || { rank: 0, score: 0, avatar: '' },
    };
  } catch (error) {
    ElMessage.error(errorMessage(error, '贡献榜加载失败'));
  } finally {
    leaderboardLoading.value = false;
  }
}

onMounted(() => {
  try {
    const saved = JSON.parse(localStorage.getItem(RECENT_PREFIX_KEY) || '[]');
    if (Array.isArray(saved)) {
      recentPrefixes.value = saved
        .map((item) => String(item || '').trim())
        .filter((item) => isCompleteGuidePrefix(item))
        .slice(0, RECENT_PREFIX_LIMIT);
    }
  } catch {
    recentPrefixes.value = [];
  }
  loadCategories();
});
</script>

<style scoped>
.product-guide-page {
  min-height: 100%;
  padding: 0 20px 24px;
  box-sizing: border-box;
  background: #f5f7fa;
}

.guide-search {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 0;
  padding-top: 96px;
}

.query-card,
.result-card {
  width: 100%;
  max-width: 580px;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid #e6eef7;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.045);
}

.query-card {
  position: relative;
  padding: 18px 20px 14px;
}

.leaderboard-entry {
  position: absolute;
  top: 12px;
  right: 14px;
  height: 26px;
  padding: 0 10px 0 8px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #eef2f7;
  border-radius: 999px;
  background: #f5f8fc;
  color: #667085;
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.16s ease, border-color 0.16s ease, color 0.16s ease;
}

.leaderboard-entry .el-icon {
  font-size: 13px;
  color: #8aa0b8;
}

.leaderboard-entry:hover {
  border-color: #d6e8fb;
  background: #eef5ff;
  color: #409eff;
}

.leaderboard-entry:hover .el-icon {
  color: #409eff;
}

.query-heading {
  text-align: center;
  margin-bottom: 14px;
}

.query-heading h2 {
  margin: 0;
  color: #102033;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.query-heading p {
  margin: 4px 0 0;
  color: #667085;
  font-size: 12px;
  line-height: 1.5;
}

.search-row {
  display: flex;
  gap: 10px;
  align-items: stretch;
}

.sn-input {
  flex: 1;
}

.sn-input :deep(.el-input__wrapper) {
  min-height: 40px;
  padding: 2px 12px;
  border-radius: 8px;
  box-shadow: 0 0 0 1px #dbe4ee inset;
  background: #ffffff;
}

.sn-input :deep(.el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #b7d4f6 inset;
}

.sn-input :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #409eff inset;
}

.sn-input :deep(.el-input__inner) {
  height: 36px;
  font-size: 15px;
  letter-spacing: 0.04em;
}

.sn-input :deep(.el-input__prefix) {
  color: #98a2b3;
}

.sn-input-icon {
  font-size: 16px;
  color: #98a2b3;
}

.sn-input :deep(.el-input__count) {
  color: #c0c4cc;
  font-size: 11px;
  background: transparent;
}

.search-submit {
  height: 40px;
  padding: 0 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
}

.query-card :deep(.search-submit:not(.is-disabled):hover),
.query-card :deep(.search-submit:not(.is-disabled):focus) {
  background-color: #337ecc;
  border-color: #337ecc;
}

.recent-searches {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.recent-label {
  color: #98a2b3;
  font-size: 12px;
}

.recent-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.recent-tag {
  height: 26px;
  padding: 0 10px;
  border: 1px solid #edf2f7;
  border-radius: 999px;
  background: #f7f9fc;
  color: #7a8699;
  font-size: 12px;
  font-family: Consolas, Monaco, monospace;
  cursor: pointer;
}

.recent-tag:hover,
.recent-tag.is-current {
  border-color: #d6e8fb;
  background: #f5f9ff;
  color: #409eff;
}

.guide-detail {
  width: min(1160px, 100%);
  margin: 0 auto;
}

.detail-header {
  margin-bottom: 12px;
  padding: 10px 16px 12px;
  border: 1px solid #e6eef7;
  border-radius: 8px;
  background: #ffffff;
}

.detail-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.detail-toolbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.nav-back,
.toolbar-add,
.toolbar-history {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  font-family: inherit;
}

.nav-back {
  height: 30px;
  min-width: 0;
  padding: 0 10px 0 6px;
  gap: 4px;
  border: 1px solid rgba(203, 213, 225, 0.9);
  border-radius: 999px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  color: #0f172a;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.06);
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.nav-back-icon {
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #ecfeff;
  color: #0891b2;
}

.nav-back-icon .el-icon {
  font-size: 12px;
}

.nav-back:hover {
  transform: translateY(-1px);
  border-color: rgba(34, 211, 238, 0.8);
  box-shadow: 0 8px 16px rgba(15, 23, 42, 0.1);
}

.toolbar-add,
.toolbar-history {
  gap: 4px;
}

.toolbar-add {
  height: 28px;
  padding: 0 10px;
  border: 1px solid #c6e2ff;
  border-radius: 6px;
  background: #f0f7ff;
  color: #3a8ee6;
  font-size: 13px;
  font-weight: 600;
}

.toolbar-add .el-icon {
  font-size: 13px;
}

.toolbar-add:hover {
  border-color: #a0cfff;
  background: #e6f1fc;
  color: #337ecc;
}

.toolbar-history {
  height: 28px;
  padding: 0 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #8a94a6;
  font-size: 13px;
}

.toolbar-history .el-icon {
  font-size: 14px;
}

.toolbar-history:hover {
  background: #f4f6f8;
  color: #5b6777;
}

.detail-overview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.detail-identity {
  min-width: 0;
}

.detail-header h2 {
  margin: 0;
  color: #102033;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.2;
}

.detail-meta-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.category-edit {
  height: 24px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #d6e8fb;
  border-radius: 999px;
  background: #f0f7ff;
  color: #3a8ee6;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}

.category-edit span {
  color: #7aa8e0;
  font-size: 11px;
  font-weight: 500;
}

.category-edit:hover {
  border-color: #b7d4f6;
  background: #e8f2ff;
  color: #337ecc;
}

.category-edit:hover span {
  color: #409eff;
}

.detail-prefix {
  height: 24px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #edf2f7;
  border-radius: 6px;
  background: #f7f9fc;
  color: #344054;
  font-size: 12px;
  font-family: Consolas, Monaco, monospace;
  letter-spacing: 0.04em;
}

.detail-prefix small {
  color: #98a2b3;
  font-size: 11px;
  font-family: inherit;
  font-weight: 600;
  letter-spacing: 0;
}

.complete-block {
  width: 240px;
  flex: 0 0 240px;
}

.complete-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 3px;
  color: #667085;
  font-size: 12px;
}

.complete-block :deep(.el-progress__text) {
  min-width: 36px;
  font-size: 12px;
  color: #667085;
}

.detail-grid {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
  gap: 12px;
  align-items: stretch;
}

.detail-group {
  padding: 12px 16px 10px;
  border: 1px solid #e6eef7;
  border-radius: 8px;
  background: #ffffff;
}

.detail-group.is-usage {
  grid-column: 1 / -1;
}

.detail-group h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
}

.detail-group h3 i {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: #94a3b8;
}

.detail-group.is-software h3 {
  color: #2b5c8f;
}

.detail-group.is-software h3 i {
  background: #7eb0e0;
}

.detail-group.is-logs h3 {
  color: #2f6f62;
}

.detail-group.is-logs h3 i {
  background: #7dccc0;
}

.detail-group.is-usage h3 {
  color: #5b4e8a;
}

.detail-group.is-usage h3 i {
  background: #b7a8e0;
}

.guide-field-row {
  display: grid;
  grid-template-columns: 138px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: start;
  padding: 8px 0;
  border-radius: 6px;
}

.guide-field-row.is-editing,
.usage-block.is-editing {
  padding: 8px;
  margin: 0 -8px;
  background: #f5f9ff;
  box-shadow: inset 0 0 0 1px #d6e8fb;
}

.usage-block.is-editing {
  padding: 10px 8px 8px;
}

.guide-field-row + .guide-field-row {
  margin-top: 2px;
}

.field-name {
  padding-top: 1px;
  color: #8a94a6;
  font-size: 12px;
  line-height: 1.5;
}

.custom-mark {
  margin-left: 6px;
  color: #c0c4cc;
  font-size: 11px;
  font-weight: 400;
}

.field-value {
  color: #102033;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

.field-value.is-path {
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
}

.field-value.is-empty {
  color: #98a2b3;
}

.field-meta {
  margin-top: 2px;
  color: #c0c4cc;
  font-size: 11px;
}

.field-actions {
  display: flex;
  gap: 2px;
  padding-top: 0;
  opacity: 0;
}

.guide-field-row:hover .field-actions,
.usage-block:hover .field-actions {
  opacity: 1;
}

.field-action {
  padding: 2px 6px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #909399;
  font-size: 12px;
  cursor: pointer;
}

.field-action.is-primary {
  color: #409eff;
}

.field-action:hover {
  background: #f2f6fc;
  color: #409eff;
}

.usage-block + .usage-block {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed #edf1f6;
}

.usage-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  color: #667085;
  font-size: 12px;
  font-weight: 700;
}

.search-empty {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #eef2f6;
  text-align: left;
}

.search-empty p {
  margin: 0;
  color: #98a2b3;
  font-size: 13px;
  line-height: 1.7;
}

.result-card {
  margin-top: 22px;
  padding: 16px 0 6px;
}

.match-tip {
  margin: 0 20px 8px;
  color: #667085;
  font-size: 13px;
  line-height: 1.6;
}

.match-list {
  display: flex;
  flex-direction: column;
}

.match-row {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  padding: 12px 20px;
  border: 0;
  border-top: 1px solid #eef2f6;
  background: transparent;
  color: #102033;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}

.match-row:hover {
  background: #f5f9ff;
}

.match-row span {
  font-weight: 500;
}

.match-arrow {
  color: #98a2b3;
  font-size: 16px;
  transition: color 0.16s ease, transform 0.16s ease;
}

.match-row:hover .match-arrow {
  color: #409eff;
  transform: translateX(2px);
}

.match-panel-enter-active {
  transition: opacity 180ms ease, transform 180ms ease;
}

.match-panel-enter-from {
  opacity: 0;
  transform: translateY(5px);
}

.category-list {
  display: grid;
  gap: 12px;
  margin-bottom: 18px;
}

.create-row {
  display: flex;
  gap: 8px;
}

.add-form :deep(.el-form-item) {
  margin-bottom: 14px;
}

.full-control {
  width: 100%;
}

.field-body :deep(.el-select) {
  width: 100%;
}

.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.board-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.board-heading-icon {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: linear-gradient(180deg, #fff6e0 0%, #f8efd8 100%);
  color: #b8860b;
  box-shadow: 0 0 0 1px rgba(184, 134, 11, 0.12);
}

.board-heading-icon .el-icon {
  font-size: 18px;
}

.board-heading-text h3 {
  margin: 0;
  color: #102033;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
}

.board-heading-text p {
  margin: 4px 0 0;
  color: #98a2b3;
  font-size: 12px;
}

.board-body {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.board-podium {
  display: grid;
  grid-template-columns: 1fr 1.16fr 1fr;
  align-items: end;
  gap: 8px;
  margin-bottom: 14px;
}

.podium-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 14px 8px 12px;
  border: 1px solid #eef2f6;
  border-radius: 12px;
  background: #ffffff;
  text-align: center;
}

.podium-card.is-silver {
  order: 1;
}

.podium-card.is-gold {
  order: 2;
  padding: 18px 8px 14px;
  border-color: #ead7a6;
  background: linear-gradient(180deg, #fffaf0 0%, #ffffff 72%);
  box-shadow: 0 10px 22px rgba(184, 134, 11, 0.1);
}

.podium-card.is-bronze {
  order: 3;
}

.podium-avatar {
  --el-avatar-bg-color: #eef4fb;
  --el-avatar-text-color: #5b7aa3;
  font-size: 15px;
  font-weight: 700;
}

.podium-avatar.has-photo {
  --el-avatar-bg-color: #f2f4f7;
}

.podium-avatar :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.podium-card.is-gold .podium-avatar {
  --el-avatar-bg-color: #f8efd8;
  --el-avatar-text-color: #8a6d2b;
  box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.22);
}

.podium-card.is-silver .podium-avatar {
  --el-avatar-bg-color: #eef1f5;
  --el-avatar-text-color: #5b6775;
}

.podium-card.is-bronze .podium-avatar {
  --el-avatar-bg-color: #f4e6dc;
  --el-avatar-text-color: #8a5a3c;
}

.podium-score {
  margin-top: 8px;
  color: #102033;
  font-size: 18px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}

.podium-card.is-gold .podium-score {
  font-size: 22px;
  color: #8a6d2b;
}

.podium-name {
  margin-top: 4px;
  color: #102033;
  font-size: 13px;
  font-weight: 600;
}

.podium-rank {
  margin-top: 2px;
  color: #98a2b3;
  font-size: 11px;
}

.podium-card.is-gold .podium-rank {
  color: #b8860b;
}

.board-list {
  display: grid;
  gap: 4px;
}

.board-list-label {
  margin: 2px 10px 4px;
  color: #98a2b3;
  font-size: 11px;
}

.board-row {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  min-height: 46px;
  padding: 6px 10px;
  border: 0;
  border-radius: 10px;
  color: #344054;
  font-size: 13px;
}

.board-row:hover {
  background: #f5f8fc;
}

.board-user {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.board-avatar {
  --el-avatar-bg-color: #e7eef7;
  --el-avatar-text-color: #4f6f94;
  flex: 0 0 28px;
  font-size: 12px;
  font-weight: 700;
}

.board-avatar :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rank {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f2f4f7;
  color: #667085;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.score {
  color: #102033;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.board-mine {
  margin-top: auto;
  padding: 14px 16px 12px;
  border: 1px solid #d6e8fb;
  border-radius: 12px;
  background: linear-gradient(180deg, #f7fbff 0%, #eef6ff 100%);
}

.board-mine-kicker {
  color: #3a8ee6;
  font-size: 12px;
  font-weight: 700;
}

.board-mine-main {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
}

.board-mine-item {
  display: grid;
  gap: 2px;
}

.board-mine-item span {
  color: #98a2b3;
  font-size: 11px;
}

.board-mine-item strong {
  color: #102033;
  font-size: 18px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.board-mine-item strong.is-score {
  color: #409eff;
}

.board-mine-hint {
  margin: 10px 0 0;
  color: #7a8699;
  font-size: 12px;
  line-height: 1.5;
}

.history-heading-icon {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #eef5ff;
  color: #409eff;
  box-shadow: 0 0 0 1px rgba(64, 158, 255, 0.12);
}

.history-heading-icon .el-icon {
  font-size: 18px;
}

.history-timeline {
  display: grid;
}

.history-item {
  position: relative;
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  gap: 12px;
  padding: 0 0 20px;
  border: 0;
  color: #344054;
}

.history-item::before {
  content: '';
  position: absolute;
  left: 15px;
  top: 34px;
  bottom: 0;
  width: 1px;
  background: #e8eef6;
}

.history-item:last-child {
  padding-bottom: 0;
}

.history-item:last-child::before {
  display: none;
}

.history-avatar {
  --el-avatar-bg-color: #e8f1ff;
  --el-avatar-text-color: #3a8ee6;
  z-index: 1;
  font-size: 13px;
  font-weight: 700;
}

.history-item.is-latest .history-avatar {
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.16);
}

.history-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.history-item strong {
  color: #102033;
  font-size: 14px;
  font-weight: 700;
}

.history-item small {
  color: #c0c4cc;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.history-action {
  margin: 6px 0 0;
  color: #667085;
  font-size: 13px;
  line-height: 1.5;
}

.history-field {
  display: inline-flex;
  align-items: center;
  height: 22px;
  margin-left: 4px;
  padding: 0 8px;
  border-radius: 999px;
  background: #f0f7ff;
  color: #3a8ee6;
  font-size: 12px;
  font-weight: 600;
  vertical-align: middle;
}

@media (max-width: 960px) {
  .query-card {
    padding: 18px 16px 14px;
  }

  .search-row {
    flex-direction: column;
  }

  .search-submit {
    width: 100%;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .detail-overview {
    flex-direction: column;
    align-items: stretch;
  }

  .complete-block {
    width: min(240px, 100%);
    flex: none;
  }

  .guide-field-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  .field-actions {
    justify-content: flex-end;
    opacity: 1;
  }
}
</style>

<style>
.leaderboard-drawer.el-drawer,
.history-drawer.el-drawer {
  background: #f6f8fb;
}

.leaderboard-drawer .el-drawer__header,
.history-drawer .el-drawer__header {
  margin-bottom: 0;
  padding: 16px 18px 12px;
  border-bottom: 1px solid #eef2f6;
}

.leaderboard-drawer .el-drawer__body,
.history-drawer .el-drawer__body {
  height: calc(100% - 73px);
  padding: 16px 16px 18px;
  overflow: auto;
}

.leaderboard-drawer .el-drawer__close-btn,
.history-drawer .el-drawer__close-btn {
  color: #98a2b3;
}
</style>
